<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex overflow-hidden">

    <AppSidebar currentPage="clients" />

    <main class="flex-1 flex flex-col h-full overflow-hidden relative">
      <!-- Top Header -->
      <header class="h-16 bg-surface border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0 z-10">
        <div class="flex items-center gap-4 w-full">
          <div class="relative w-96 group">
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors material-symbols-outlined">search</span>
            <input
              v-model="searchQuery"
              class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400"
              placeholder="Search clients by name, email, or ID..."
              type="text"
            />
          </div>
        </div>
      </header>

      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto bg-background-light p-8">
        <div class="max-w-[1400px] mx-auto space-y-8">

          <!-- Page Header & Actions -->
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 class="text-2xl font-bold text-slate-900 tracking-tight">Client Overview</h2>
              <p class="text-slate-500 mt-1">Manage client portfolios, tax statuses, and recent activity.</p>
            </div>
            <div class="flex gap-3">
              <button
                @click="openAddClientModal"
                class="px-4 py-2.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-all flex items-center gap-2">
                <span class="material-symbols-outlined" style="font-size: 20px;">person_add</span>
                Add Client
              </button>
              <button
                @click="openCreateTemplateModal"
                class="px-4 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover shadow-md shadow-primary/20 transition-all flex items-center gap-2">
                <span class="material-symbols-outlined" style="font-size: 20px;">add_task</span>
                Create Task Template
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex items-center justify-center py-12">
            <div class="text-center">
              <svg class="animate-spin h-12 w-12 text-primary mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-slate-500">Loading clients...</p>
            </div>
          </div>

          <template v-else>
            <!-- Client Table -->
            <div class="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <!-- Table Toolbar -->
              <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
                <div class="flex items-center gap-2">
                  <button
                    @click="toggleFilter"
                    class="px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-2"
                    :class="showFilter ? 'bg-primary/10 text-primary border-primary/30' : 'text-slate-600 bg-slate-50 hover:bg-slate-100 border-slate-200'">
                    <span class="material-symbols-outlined" style="font-size: 18px;">filter_list</span>
                    Filter
                    <span v-if="activeFilter" class="h-1.5 w-1.5 rounded-full bg-primary"></span>
                  </button>
                  <div v-if="showFilter" class="flex items-center gap-2">
                    <select v-model="activeFilter"
                      class="px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                      <option value="">All Statuses</option>
                      <option value="Active">Active</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Pending">Pending</option>
                      <option value="Review Pending">Review Pending</option>
                      <option value="Docs Missing">Docs Missing</option>
                      <option value="Filed">Filed</option>
                      <option value="Completed">Completed</option>
                      <option value="New">New</option>
                    </select>
                    <button v-if="activeFilter" @click="activeFilter = ''" class="text-xs text-primary hover:underline">Clear</button>
                  </div>
                  <button
                    @click="toggleSort"
                    class="px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors flex items-center gap-2">
                    <span class="material-symbols-outlined" style="font-size: 18px;">swap_vert</span>
                    Sort: {{ sortLabel }}
                  </button>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-slate-500 font-medium">
                    Showing {{ filteredClients.length }} of {{ allClients.length }} clients
                  </span>
                  <button @click="loadClients" class="p-1.5 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-primary/10">
                    <span class="material-symbols-outlined" style="font-size: 18px;">refresh</span>
                  </button>
                </div>
              </div>

              <!-- Empty State -->
              <div v-if="filteredClients.length === 0" class="text-center py-16">
                <span class="material-symbols-outlined text-slate-300" style="font-size: 56px;">group</span>
                <p class="text-slate-500 mt-3 font-medium">No clients found</p>
                <p class="text-slate-400 text-sm mt-1">Try adjusting your search or filter</p>
              </div>

              <!-- Table -->
              <div v-else class="overflow-x-auto custom-scrollbar">
                <table class="w-full text-left border-collapse">
                  <thead>
                    <tr class="bg-slate-50/80 text-slate-500 text-xs uppercase tracking-wider font-semibold border-b border-slate-200">
                      <th class="px-6 py-4 cursor-pointer hover:text-primary" @click="setSortField('name')">
                        Client Name
                        <span v-if="sortField === 'name'" class="material-symbols-outlined align-middle" style="font-size: 14px;">
                          {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                        </span>
                      </th>
                      <th class="px-6 py-4">Entity Type</th>
                      <th class="px-6 py-4 cursor-pointer hover:text-primary" @click="setSortField('status')">
                        Status
                        <span v-if="sortField === 'status'" class="material-symbols-outlined align-middle" style="font-size: 14px;">
                          {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                        </span>
                      </th>
                      <th class="px-6 py-4">Assigned CPA</th>
                      <th class="px-6 py-4 cursor-pointer hover:text-primary" @click="setSortField('lastActive')">
                        Last Active
                        <span v-if="sortField === 'lastActive'" class="material-symbols-outlined align-middle" style="font-size: 14px;">
                          {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                        </span>
                      </th>
                      <th class="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-100">
                    <tr
                      v-for="client in filteredClients"
                      :key="client.id"
                      @click="viewClientDetails(client)"
                      class="hover:bg-slate-50/50 transition-colors group cursor-pointer">
                      <td class="px-6 py-4">
                        <div class="flex items-center gap-3">
                          <div class="h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" :class="client.avatarClass">
                            {{ client.initials }}
                          </div>
                          <div>
                            <p class="text-sm font-medium text-slate-900">{{ client.name }}</p>
                            <p class="text-xs text-slate-500">{{ client.id }}</p>
                          </div>
                        </div>
                      </td>
                      <td class="px-6 py-4">
                        <span class="text-sm text-slate-600">{{ client.entityType }}</span>
                      </td>
                      <td class="px-6 py-4">
                        <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" :class="client.statusClass">
                          <span class="h-1.5 w-1.5 rounded-full" :class="client.statusDotClass"></span>
                          {{ client.status }}
                        </span>
                      </td>
                      <td class="px-6 py-4">
                        <div class="flex items-center gap-2">
                          <div class="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                            {{ getInitials(client.cpaName) }}
                          </div>
                          <span class="text-sm text-slate-600">{{ client.cpaName }}</span>
                        </div>
                      </td>
                      <td class="px-6 py-4">
                        <span class="text-sm text-slate-500">{{ client.lastActive }}</span>
                      </td>
                      <td class="px-6 py-4 text-right">
                        <div class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            @click.stop="viewClientDetails(client)"
                            class="p-1.5 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-primary/10"
                            title="View Details">
                            <span class="material-symbols-outlined" style="font-size: 18px;">open_in_new</span>
                          </button>
                          <button
                            @click.stop="openSendMessage(client)"
                            class="p-1.5 text-slate-400 hover:text-blue-500 transition-colors rounded-lg hover:bg-blue-50"
                            title="Send Message">
                            <span class="material-symbols-outlined" style="font-size: 18px;">chat</span>
                          </button>
                          <button
                            @click.stop="openActionsMenu(client, $event)"
                            class="p-1.5 text-slate-400 hover:text-primary transition-colors rounded-lg hover:bg-primary/10"
                            title="More Actions">
                            <span class="material-symbols-outlined" style="font-size: 18px;">more_vert</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>
      </div>
    </main>

    <!-- ADD CLIENT MODAL -->
    <div v-if="showAddClientModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">person_add</span>
            Add New Client
          </h3>
          <button @click="showAddClientModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Full Name / Company *</label>
            <input v-model="newClient.name" type="text" placeholder="e.g. John Doe or Acme Corp"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input v-model="newClient.email" type="email" placeholder="email@example.com"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Phone</label>
              <input v-model="newClient.phone" type="tel" placeholder="+1 (555) 000-0000"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Entity Type</label>
              <input v-model="newClient.serviceType" type="text" list="entityTypeList"
                placeholder="Select or type an entity type"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white" />
              <datalist id="entityTypeList">
                <option v-for="t in entityTypeOptions" :key="t" :value="t"></option>
              </datalist>
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Tax Year</label>
              <select v-model="newClient.taxYear"
                class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                <option v-for="y in taxYearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
            </div>
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Filing Deadline</label>
            <input v-model="newClient.deadline" type="date"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div v-if="addClientError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ addClientError }}
          </div>
          <div v-if="addClientSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ addClientSuccess }}
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showAddClientModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="addClient" :disabled="addingClient"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="addingClient" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ addingClient ? 'Adding...' : 'Add Client' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SEND MESSAGE MODAL -->
    <div v-if="showMessageModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">send</span>
            Send Message to {{ selectedClient?.name }}
          </h3>
          <button @click="showMessageModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Subject</label>
            <input v-model="messageForm.subject" type="text" placeholder="Message subject..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Message</label>
            <textarea v-model="messageForm.message" rows="4" placeholder="Write your message..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"></textarea>
          </div>
          <div class="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <p class="text-sm font-medium text-slate-700">Also send via email</p>
              <p class="text-xs text-slate-400">Send email notification to client</p>
            </div>
            <button @click="messageForm.sendEmail = !messageForm.sendEmail"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
              :class="messageForm.sendEmail ? 'bg-primary' : 'bg-slate-300'">
              <span class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
                :class="messageForm.sendEmail ? 'translate-x-6' : 'translate-x-1'"></span>
            </button>
          </div>
          <div v-if="messageError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">error</span>{{ messageError }}
          </div>
          <div v-if="messageSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <span class="material-symbols-outlined" style="font-size: 16px;">check_circle</span>{{ messageSuccess }}
          </div>
          <div class="flex gap-3 pt-2">
            <button @click="showMessageModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="sendMessage" :disabled="sendingMessage"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="sendingMessage" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ sendingMessage ? 'Sending...' : 'Send Message' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ CREATE TASK TEMPLATE MODAL — now saves to SharePoint -->
    <div v-if="showTemplateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div class="flex items-center justify-between mb-5">
          <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
            <span class="material-symbols-outlined text-primary" style="font-size: 20px;">add_task</span>
            Create Task Template
          </h3>
          <button @click="showTemplateModal = false" class="text-slate-400 hover:text-slate-600">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="space-y-4">

          <!-- Template Name -->
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Template Name *</label>
            <input v-model="templateForm.name" type="text" placeholder="e.g. Individual Tax Return 2025"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>

          <!-- ✅ Entity Type — required by SharePoint saveTemplate() -->
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Entity Type *</label>
            <select v-model="templateForm.entityType"
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
              <option value="Individual">Individual</option>
              <option value="LLC">LLC</option>
              <option value="S-Corp">S-Corp</option>
              <option value="C-Corp">C-Corp</option>
              <option value="Partnership">Partnership</option>
            </select>
          </div>

          <!-- ✅ Description — stored in SharePoint Description field -->
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
            <input v-model="templateForm.description" type="text" placeholder="Optional description..."
              class="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
          </div>

          <!-- Tasks -->
          <div>
            <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tasks *</label>
            <div class="space-y-2 max-h-56 overflow-y-auto pr-1">
              <div v-for="(task, index) in templateForm.tasks" :key="index"
                class="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div class="flex items-center gap-2">
                  <input v-model="task.title" type="text" :placeholder="`Task ${index + 1} title`"
                    class="flex-1 px-2 py-1.5 bg-white border border-slate-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary" />
                  <button @click="removeTemplateTask(index)" class="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                    <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
                  </button>
                </div>
                <!-- ✅ Task meta row — daysUntilDue + priority + category -->
                <div class="grid grid-cols-3 gap-2">
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">Due in days</label>
                    <input v-model.number="task.daysUntilDue" type="number" min="1" placeholder="7"
                      class="w-full px-2 py-1 bg-white border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">Priority</label>
                    <select v-model="task.priority"
                      class="w-full px-2 py-1 bg-white border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary/30">
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Normal">Normal</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">Category</label>
                    <select v-model="task.category"
                      class="w-full px-2 py-1 bg-white border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary/30">
                      <option value="General">General</option>
                      <option value="Tax Preparation">Tax Prep</option>
                      <option value="Document Collection">Docs</option>
                      <option value="Compliance">Compliance</option>
                      <option value="Accounting">Accounting</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <button @click="addTemplateTask"
              class="mt-2 w-full py-2 rounded-lg border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1">
              <span class="material-symbols-outlined" style="font-size: 18px;">add</span>
              Add Task
            </button>
          </div>

          <div v-if="templateError" class="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{{ templateError }}</div>
          <div v-if="templateSuccess" class="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{{ templateSuccess }}</div>

          <div class="flex gap-3 pt-2">
            <button @click="showTemplateModal = false"
              class="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
            <button @click="saveTemplate" :disabled="savingTemplate"
              class="flex-1 py-2.5 rounded-lg bg-primary text-secondary text-sm font-bold hover:bg-primary-hover transition-all flex items-center justify-center gap-2 disabled:opacity-50">
              <svg v-if="savingTemplate" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ savingTemplate ? 'Saving...' : 'Save Template' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ACTIONS CONTEXT MENU -->
    <div v-if="showActionsMenu && actionMenuClient"
      class="fixed z-50 bg-white rounded-xl shadow-xl border border-slate-200 py-1 w-48"
      :style="{ top: actionMenuY + 'px', left: actionMenuX + 'px' }"
      v-click-outside="closeActionsMenu">
      <button @click="viewClientDetails(actionMenuClient); closeActionsMenu()"
        class="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
        <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">open_in_new</span>
        View Details
      </button>
      <button @click="openSendMessage(actionMenuClient); closeActionsMenu()"
        class="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
        <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">chat</span>
        Send Message
      </button>
      <button @click="editClient(actionMenuClient); closeActionsMenu()"
        class="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3">
        <span class="material-symbols-outlined text-slate-400" style="font-size: 18px;">edit</span>
        Edit Client
      </button>
      <div class="border-t border-slate-100 my-1"></div>
      <button @click="confirmDeleteClient(actionMenuClient); closeActionsMenu()"
        class="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3">
        <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
        Remove Client
      </button>
    </div>

  </div>
</template>

<script>
import AppSidebar from '../components/AppSidebar.vue';

// Australian FY ends 30 June: before July we're still lodging the prior year
function buildTaxYearOptions() {
  const now = new Date();
  const currentFY = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return Array.from({ length: 4 }, (_, i) => String(currentFY - i));
}

const FALLBACK_ENTITY_TYPES = ['Individual', 'Sole Trader', 'Company',
  'Partnership', 'Trust', 'SMSF'];

export default {
  name: 'AdminClientList',
  components: { AppSidebar },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) binding.value();
        };
        document.addEventListener('click', el._clickOutsideHandler);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutsideHandler);
      }
    }
  },

  data() {
    return {
      loading: true,
      searchQuery: '',
      activeFilter: '',
      showFilter: false,
      sortField: 'name',
      sortDir: 'asc',

      allClients: [],

      stats: {
        pendingReturns: 0,
        unreadMessages: 0,
        activeClients: 0
      },

      // Add Client Modal
      showAddClientModal: false,
      addingClient: false,
      addClientError: '',
      addClientSuccess: '',
      newClient: {
        name: '', email: '', phone: '',
        serviceType: 'Individual', taxYear: buildTaxYearOptions()[0], deadline: ''
      },

      entityTypeOptions: FALLBACK_ENTITY_TYPES,

      // Send Message Modal
      showMessageModal: false,
      sendingMessage: false,
      messageError: '',
      messageSuccess: '',
      selectedClient: null,
      messageForm: { subject: '', message: '', sendEmail: false },

      // ✅ Task Template Modal — all fields SharePoint needs
      showTemplateModal: false,
      savingTemplate: false,
      templateError: '',
      templateSuccess: '',
      templateForm: {
        name: '',
        entityType: 'Individual',   // ✅ required by saveTemplate()
        description: '',            // ✅ stored in SharePoint Description field
        tasks: [
          { title: '', priority: 'Medium', daysUntilDue: 7, category: 'General' },
          { title: '', priority: 'Medium', daysUntilDue: 7, category: 'General' }
        ]
      },

      // Context Menu
      showActionsMenu: false,
      actionMenuClient: null,
      actionMenuX: 0,
      actionMenuY: 0
    };
  },

  computed: {
    filteredClients() {
      let list = [...this.allClients];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(c =>
          c.name.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          c.entityType.toLowerCase().includes(q)
        );
      }
      if (this.activeFilter) {
        list = list.filter(c => c.status === this.activeFilter);
      }
      list.sort((a, b) => {
        let valA = a[this.sortField] || '';
        let valB = b[this.sortField] || '';
        if (this.sortDir === 'asc') return String(valA).localeCompare(String(valB));
        return String(valB).localeCompare(String(valA));
      });
      return list;
    },

    sortLabel() {
      const labels = { name: 'Name', status: 'Status', lastActive: 'Recent' };
      return labels[this.sortField] || 'Name';
    },

    taxYearOptions() {
      return buildTaxYearOptions();
    }
  },

  async mounted() {
    await this.loadClients();

    try {
      const choices = await this.$sharepoint.getEntityTypeChoices();
      if (choices && choices.length) this.entityTypeOptions = choices;
    } catch (e) {
      console.error('Error loading entity type choices:', e);
    }
  },

  methods: {
    async loadClients() {
      try {
        this.loading = true;
        const response = await this.$sharepoint.getClients();
        console.log('Loaded clients:', response.length);

        const avatarClasses = [
          'bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700',
          'bg-teal-100 text-teal-700', 'bg-indigo-100 text-indigo-700',
          'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700',
          'bg-green-100 text-green-700'
        ];

        this.allClients = response.map((client, index) => ({
          id: `#C-${client.Id}`,
          rawId: client.Id,
          name: client.Title,
          initials: this.getInitials(client.Title),
          avatarClass: avatarClasses[index % avatarClasses.length],
          entityType: client.CompanyName || client.ServiceType || 'Individual',
          status: client.Status || 'New',
          statusClass: this.getStatusClass(client.Status),
          statusDotClass: this.getStatusDotClass(client.Status),
          cpaName: client.AuthorName || 'Unassigned',
          lastActive: this.formatDate(client.Modified),
          email: client.Email || '',
          phone: client.Phone || ''
        }));

        this.stats.activeClients = this.allClients.length;
        this.stats.pendingReturns = this.allClients.filter(c =>
          ['Review Pending', 'Pending'].includes(c.status)
        ).length;
        this.stats.unreadMessages = 0;
      } catch (error) {
        console.error('Error loading clients:', error);
      } finally {
        this.loading = false;
      }
    },

    viewClientDetails(client) {
      this.$router.push(`/admin/clients/${client.rawId}`);
    },

    toggleFilter() { this.showFilter = !this.showFilter; },

    setSortField(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDir = 'asc';
      }
    },

    toggleSort() {
      const fields = ['name', 'status', 'lastActive'];
      const currentIndex = fields.indexOf(this.sortField);
      this.sortField = fields[(currentIndex + 1) % fields.length];
      this.sortDir = 'asc';
    },

    // ---- Add Client ----
    openAddClientModal() {
      this.addClientError = '';
      this.addClientSuccess = '';
      this.newClient = { name: '', email: '', phone: '', serviceType: 'Individual', taxYear: this.taxYearOptions[0], deadline: '' };
      this.showAddClientModal = true;
    },

    async addClient() {
      this.addClientError = '';
      this.addClientSuccess = '';
      if (!this.newClient.name.trim()) { this.addClientError = 'Client name is required.'; return; }
      this.addingClient = true;
      try {
        await this.$sharepoint.addClient(this.newClient);
        this.addClientSuccess = `✅ "${this.newClient.name}" added successfully!`;
        setTimeout(() => {
          this.showAddClientModal = false;
          this.loadClients();
        }, 1500);
      } catch (error) {
        this.addClientError = 'Failed to add client. Please try again.';
        console.error(error);
      } finally {
        this.addingClient = false;
      }
    },

    // ---- Send Message ----
    openSendMessage(client) {
      this.selectedClient = client;
      this.messageForm = { subject: '', message: '', sendEmail: false };
      this.messageError = '';
      this.messageSuccess = '';
      this.showMessageModal = true;
    },

    async sendMessage() {
      this.messageError = '';
      this.messageSuccess = '';
      if (!this.messageForm.subject.trim()) { this.messageError = 'Subject is required.'; return; }
      if (!this.messageForm.message.trim()) { this.messageError = 'Message cannot be empty.'; return; }
      this.sendingMessage = true;
      try {
        const user = this.$sharepoint.getCurrentUser();
        await this.$sharepoint.sendClientMessage({
          subject: this.messageForm.subject,
          message: this.messageForm.message,
          clientName: this.selectedClient.name,
          sender: user?.name || user?.username || 'Admin',
          sendEmail: this.messageForm.sendEmail
        });
        this.messageSuccess = '✅ Message sent successfully!';
        setTimeout(() => { this.showMessageModal = false; }, 1500);
      } catch (error) {
        this.messageError = 'Failed to send message. Please try again.';
        console.error(error);
      } finally {
        this.sendingMessage = false;
      }
    },

    // ---- Task Template ----
    openCreateTemplateModal() {
      this.templateError = '';
      this.templateSuccess = '';
      this.templateForm = {
        name: '',
        entityType: 'Individual',
        description: '',
        tasks: [
          { title: '', priority: 'Medium', daysUntilDue: 7, category: 'General' },
          { title: '', priority: 'Medium', daysUntilDue: 7, category: 'General' }
        ]
      };
      this.showTemplateModal = true;
    },

    addTemplateTask() {
      this.templateForm.tasks.push({
        title: '', priority: 'Medium', daysUntilDue: 7, category: 'General'
      });
    },

    removeTemplateTask(index) {
      this.templateForm.tasks.splice(index, 1);
    },

    async saveTemplate() {
      this.templateError = '';
      this.templateSuccess = '';

      if (!this.templateForm.name.trim()) {
        this.templateError = 'Template name is required.';
        return;
      }

      // ✅ Filter out blank tasks before saving
      const validTasks = this.templateForm.tasks.filter(t => t.title.trim());
      if (validTasks.length === 0) {
        this.templateError = 'Add at least one task with a title.';
        return;
      }

      this.savingTemplate = true;
      try {
        // ✅ Actually calls SharePoint — was a fake timeout before
        const result = await this.$sharepoint.saveTemplate({
          name: this.templateForm.name,
          entityType: this.templateForm.entityType,
          description: this.templateForm.description || '',
          tasks: validTasks
        });

        console.log('Template saved to SharePoint:', result);
        this.templateSuccess = `✅ Template "${this.templateForm.name}" saved with ${validTasks.length} tasks!`;
        setTimeout(() => { this.showTemplateModal = false; }, 1500);
      } catch (error) {
        this.templateError = `Failed to save template: ${error.message}`;
        console.error('Error saving template:', error);
      } finally {
        this.savingTemplate = false;
      }
    },

    // ---- Context Menu ----
    openActionsMenu(client, event) {
      this.actionMenuClient = client;
      this.actionMenuX = Math.min(event.clientX, window.innerWidth - 200);
      this.actionMenuY = Math.min(event.clientY + 8, window.innerHeight - 200);
      this.showActionsMenu = true;
    },

    closeActionsMenu() {
      this.showActionsMenu = false;
      this.actionMenuClient = null;
    },

    editClient(client) {
      this.$router.push(`/admin/clients/${client.rawId}`);
    },

    async confirmDeleteClient(client) {
      const clientId = client.rawId || client.Id || client.id;
      const clientName = client.name || client.Title;

      if (!confirm(`Remove "${clientName}" from the system? This cannot be undone.`)) return;
      if (!clientId) { alert('Cannot delete: missing SharePoint ID.'); return; }

      // Step 1 — SharePoint
      try {
        await this.$sharepoint.deleteClient(clientId);
        console.log('Client deleted from SharePoint:', clientId);
      } catch (error) {
        console.error('Failed to delete client:', error);
        alert('Failed to delete client from SharePoint. Please try again.');
        return;
      }

      // Step 2 — UI only (✅ was wrongly using this.clientList — fixed to this.allClients)
      try {
        this.allClients = this.allClients.filter(
          c => (c.rawId || c.Id || c.id) !== clientId
        );
        this.stats.activeClients = this.allClients.length;
      } catch (uiError) {
        console.warn('UI update failed, reloading:', uiError);
        await this.loadClients();
      }
    },

    // ---- Helpers ----
    getInitials(name) {
      if (!name) return '??';
      const parts = name.split(' ');
      return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },

    getStatusClass(status) {
      const map = {
        'Active': 'bg-green-50 text-green-700 border-green-100',
        'Pending': 'bg-amber-50 text-amber-700 border-amber-100',
        'Review Pending': 'bg-amber-50 text-amber-700 border-amber-100',
        'Completed': 'bg-green-50 text-green-700 border-green-100',
        'Filed': 'bg-green-50 text-green-700 border-green-100',
        'In Progress': 'bg-blue-50 text-blue-700 border-blue-100',
        'Docs Missing': 'bg-red-50 text-red-700 border-red-100',
        'New': 'bg-slate-50 text-slate-600 border-slate-200'
      };
      return map[status] || 'bg-gray-50 text-gray-700 border-gray-100';
    },

    getStatusDotClass(status) {
      const map = {
        'Active': 'bg-green-500', 'Pending': 'bg-amber-500',
        'Review Pending': 'bg-amber-500', 'Completed': 'bg-green-500',
        'Filed': 'bg-green-500', 'In Progress': 'bg-blue-500',
        'Docs Missing': 'bg-red-500', 'New': 'bg-slate-400'
      };
      return map[status] || 'bg-gray-500';
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / 86400000);
      if (diffDays === 0) {
        const diffHours = Math.floor(diffTime / 3600000);
        return diffHours < 1 ? 'Just now' : `${diffHours}h ago`;
      }
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
      return date.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
</style>